// 顧客サイト（テナント店舗）向けの広告コンバージョン計測。
//
// 店舗オーナーが <head> に設定した GTM(dataLayer) / GA4(gtag) / Meta Pixel(fbq)
// へ標準イベントを流すだけ。Google 広告コンバージョン(AW-)等への紐付けは各店舗が
// 自分の GTM 側で行う前提なので、JoyBase は標準イベントの発火に徹する。
//
// 注意:
// - タグは独自ドメイン店の <head> にしか注入されない（advertising_tags_helper の制約）。
//   未注入の店舗では window.gtag/fbq/dataLayer が存在せず、各送信は no-op になる。
// - 完了画面はポーリングや StrictMode で複数回評価されるため、transaction_id を
//   キーに sessionStorage で「1 取引 1 回だけ」発火させる（fireOnce）。
// - gtag は configureCustomerAnalytics で渡したテナント GA4 測定ID にのみ send_to で
//   限定送信する。JoyBase 自身の GA（index.html.erb の GOOGLE_ANALYTICS_ID）には流さない。

type PurchaseParams = {
  value?: number;
  currency?: string;
  transactionId?: string;
};

declare global {
  interface Window {
    dataLayer?: Record<string, unknown>[];
    gtag?: (...args: unknown[]) => void;
    fbq?: (...args: unknown[]) => void;
  }
}

const pushDataLayer = (event: string, params: Record<string, unknown>): void => {
  if (typeof window === 'undefined') return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event, ...params });
};

// テナント店舗の GA4 測定ID。設定された時だけ、その測定IDに限定して gtag を撃つ。
// （customer/index.html.erb が本番で注入する JoyBase 自身の GA4 に、テナントの
//  購入/登録イベントが流入するのを防ぐ。未設定なら gtag は撃たない。）
let tenantGa4Id: string | null = null;

export const configureCustomerAnalytics = (opts: { ga4Id?: string | null }): void => {
  tenantGa4Id = opts.ga4Id || null;
};

const fireGtag = (event: string, params: Record<string, unknown>): void => {
  if (!tenantGa4Id) return;
  if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
    window.gtag('event', event, { ...params, send_to: tenantGa4Id });
  }
};

const fireFbq = (event: string, params: Record<string, unknown>, eventId?: string): void => {
  if (typeof window !== 'undefined' && typeof window.fbq === 'function') {
    // eventID を渡し、新規タブ再発火や将来の CAPI 併用時に Meta 側で重複排除可能にする。
    if (eventId) {
      window.fbq('track', event, params, { eventID: eventId });
    } else {
      window.fbq('track', event, params);
    }
  }
};

// 同一キーでの二重発火を防ぐ（ポーリングの complete 連打・再レンダリング・StrictMode 対策）。
const fireOnce = (key: string, fn: () => void): void => {
  if (typeof window === 'undefined') return;
  try {
    if (sessionStorage.getItem(key) === '1') return;
    sessionStorage.setItem(key, '1');
  } catch {
    // プライベートブラウズ等で sessionStorage が使えなくてもイベントは通す
  }
  fn();
};

/**
 * 顧客登録完了（メール認証完了時）。
 * @param dedupKey 認証トークン等、登録 1 件を一意に表す値
 */
export const trackSignUp = (dedupKey: string): void => {
  if (!dedupKey) return;
  fireOnce(`cv_signup_${dedupKey}`, () => {
    pushDataLayer('sign_up', {});
    fireGtag('sign_up', {});
    fireFbq('CompleteRegistration', {});
  });
};

/**
 * 購入完了（ポイント購入 / くじ購入）。
 * リダイレクト直後ではなく、ポーリングで決済確定(completed)を検知した地点で呼ぶこと。
 */
export const trackPurchase = ({ value, currency = 'JPY', transactionId }: PurchaseParams): void => {
  // 安定した取引IDが無ければ計測しない（dedup キーの共有汚染と再発火を防ぐ）。
  if (!transactionId) return;
  fireOnce(`cv_purchase_${transactionId}`, () => {
    const params = { value: value ?? 0, currency, transaction_id: transactionId };
    pushDataLayer('purchase', params);
    fireGtag('purchase', params);
    fireFbq('Purchase', { value: value ?? 0, currency }, transactionId);
  });
};
